/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import { DownloadIcon, SearchIcon } from '../../../assets/icons';
import { formatAmount, fromStorage, getUpdatedState } from '../../../utils';
import { Input, Render } from '../../../components';
import { ReportService } from '../../../services';

const ReportBillingRoute = () => {
  const { data, filters, onChange, downloadAll, downloadOne, reload, search } = useFetchTable();

  return (
    <main className="w-full">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex flex-1 flex-wrap items-end gap-3">
          <Input
            className={{ base: 'max-w-60' }}
            placeholder="Buscar nombre, CI o RIF"
            value={filters.search}
            onValueChange={v => onChange('search', v)}
            prepend={
              <img src={SearchIcon} className="w-4 h-4" alt="" />
            }
          />

          <Input
            className={{ base: 'max-w-60' }}
            placeholder="Ruta"
            value={filters.route}
            onValueChange={v => onChange('route', v)}
          />

          <Input
            className={{ base: 'max-w-44' }}
            type="date"
            label="Desde"
            value={filters.since}
            onValueChange={v => onChange('since', v)}
          />

          <Input
            className={{ base: 'max-w-44' }}
            type="date"
            label="Hasta"
            value={filters.until}
            onValueChange={v => onChange('until', v)}
          />

          <div className="flex gap-4">
            <button
              className="bg-red hover:bg-red-700 text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
              onClick={() => search()}
            >
              Filtrar
            </button>
            <button
              className="bg-black hover:bg-gray-800 text-white px-6 py-2 rounded-full text-sm font-medium transition-colors"
              onClick={() => reload()}
            >
              Limpiar
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden mt-6">
        <div className="flex items-center justify-between py-4 px-3">
          <p className="text-2xl font-bold text-center">Historial de utilidad y rentabilidad</p>

          <button
            className="flex items-center gap-2 bg-black text-white px-4 py-1.5 rounded-lg font-semibold"
            onClick={() => toast.promise(downloadAll(), { loading: 'Descargando...', success: 'Reporte descargado con éxito', error: 'Error al descargar el reporte' })}
          >
            <img src={DownloadIcon} className="w-4 h-4 brightness-200" alt="" />
            Descargar
          </button>
        </div>

        <div className="overflow-x-auto px-4 pb-4">
          <div className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_6rem] gap-2 mb-4 text-[11px] [&>div]:text-sm items-end font-bold text-default whitespace-pre-line">
            <div>Ruta</div>
            <div>Código</div>
            <div>Facturación por{'\n'}traslado ($)</div>
            <div>Costo de{'\n'}traslado ($)</div>
            <div>Deducción{'\n'}(11,20%) (Bs)</div>
            <div>Utilidad ($)</div>
            <div>Rentabilidad por{'\n'}traslado ($)</div>
            <div>Fecha próximo{'\n'}ajuste ($)</div>
            <div className="text-center">Acción</div>
          </div>

          <div className="space-y-3">
            {data.map((item, index) => {
              const utility = Number(item?.amount) - Number(item?.amount_cost) - Number(item?.amount_deduction);
              const profit = (utility / item.amount) * 100;
              return (
                <>
                  <Render condition={!index || item?.order?.user_id !== data.at(index - 1)?.order?.user_id}>
                    <div className="bg-[#242424] text-white font-semibold py-3 rounded-xl text-base">
                      <div className="pl-2">{ item?.order?.user?.person?.company?.name }</div>
                    </div>
                  </Render>

                  <div
                    key={item.id}
                    className="grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_1fr_1fr_6rem] gap-y-2 items-center [&>div]:py-3 [&>div]:bg-[#F2F2F2] rounded-xl hover:shadow-md transition-shadow text-sm"
                  >
                    <div className="pl-2 rounded-l-lg">{ item?.order?.route?.name }</div>
                    <div>{ String(item?.order?.route?.id).padStart(4, '0') }</div>
                    <div>{ formatAmount(item?.amount) }</div>
                    <div>{ formatAmount(item?.amount_cost) }</div>
                    <div>{ formatAmount(item?.amount_deduction) }</div>
                    <div>{ formatAmount(utility) }</div>
                    <div>{ formatAmount(profit) }</div>
                    <div>{ moment(item?.next_adjustment_at).format('DD/MM/YYYY') }</div>
                    <div className="flex items-center justify-center gap-3 rounded-r-lg">
                      <img
                        src={DownloadIcon}
                        className="w-5 h-5 object-contain cursor-pointer"
                        onClick={() => {
                          toast.promise(
                            downloadOne(item?.id),
                            {
                              loading: 'Descargando...',
                              success: 'Reporte impreso con éxito',
                              error: 'Error al imprimir el reporte',
                            }
                          );
                        }}
                        alt=""
                      />
                    </div>
                  </div>
                </>
              )
            })}
          </div>

          {!data.length && (
            <p className="py-8 text-text text-center">Sin datos para mostrar</p>
          )}
        </div>
      </div>
    </main>
  );
};

const useFetchTable = () => {
  const initialFilters = {
    search: '',
    route: '',
    since: '',
    until: '',
  };

  const [data, setData] = useState([]);
  const [canFetch, setCanFetch] = useState(true);
  const [filters, setFilters] = useState(initialFilters);

  const fetchData = async () => {
    if (!canFetch) return;
    setCanFetch(false);

    try {
      const payload = await getUpdatedState(setFilters);
      const response = await ReportService.admin.billing.findByProfitAll(payload);

      setData(response.data);
      setCanFetch(true);

    } catch (error) {
      setData([]);
      setCanFetch(true);
    }
  }

  const downloadAll = async () => {
    try {
      const response = await ReportService.admin.billing.downloadByProfitAll(filters);
      if (response?.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }

    } catch (error) {
      console.log(error);
    }
  }

  const downloadOne = async (id) => {
    try {
      const response = await ReportService.admin.billing.downloadByProfitOne(id);
      if (response?.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.log(error);
    }
  }

  const reload = (inSamePage = false) => {
    setCanFetch(true);
    if (!inSamePage) setFilters(initialFilters);
    fetchData();
  }

  const onChange = (target, value) => {
    setFilters(s => ({ ...s, [target]: value }));
  }

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    downloadAll,
    downloadOne,
    filters,
    isLoading: !canFetch,
    onChange,
    reload,
    search: fetchData,
  }
}

export default ReportBillingRoute;